/**
 * @module useAppBarActions
 * Singleton that lets any page component inject action buttons into the app bar.
 *
 * Pattern: pages call `setActions([...])` on mount and `clearActions()` on
 * unmount (via onBeforeUnmount). The AppBar component renders the `actions`
 * array as buttons in the right side of the bar.
 *
 * Lifecycle note: When Vue Router navigates from page A to page B, the order is:
 *   1. Page B's setup() runs (watchEffect/setActions fires)
 *   2. Page A's onBeforeUnmount fires (clearActions)
 *   3. Page B's onMounted fires
 * This means a naive clearActions would wipe actions that were just set by B.
 * To handle this, clearActions defers to nextTick and checks whether setActions
 * was called in the same tick — if so, the clear is skipped.
 *
 * Example action shape:
 *   { icon: "mdi-export", label: "Export", onClick: () => exportCsv() }
 */

import { ref, nextTick } from "vue";

// Shared across all usages — only one page is active at a time
const actions = ref([]);

// Tracks whether setActions was called in the current tick (guards against
// stale clearActions from a departing page wiping the incoming page's actions)
let setCalledThisTick = false;

export function useAppBarActions() {
  function setActions(newActions) {
    setCalledThisTick = true;
    actions.value = newActions;
  }

  /**
   * Clear all actions. Deferred to nextTick so that if a new page already
   * called setActions during its setup, the clear is skipped.
   */
  function clearActions() {
    nextTick(() => {
      if (!setCalledThisTick) {
        actions.value = [];
      }
      setCalledThisTick = false;
    });
  }

  return { actions, setActions, clearActions };
}
